import { useEffect, useRef } from 'react';

interface IOptions {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  // 감시 기준이 되는 스크롤 컨테이너. 미지정 시 viewport 기준.
  root?: React.RefObject<HTMLElement | null> | null;
  // viewport(또는 root) 경계를 확장해 sentinel을 미리 감지하기 위한 여백
  rootMargin?: string;
}

/**
 * IntersectionObserver 기반 무한스크롤 훅.
 *
 * ─────────────────────────────────────────────────────────────
 * # 동작 원리
 *
 * 1. `sentinelRef`를 리스트 끝의 빈 `<div>`에 부착 (높이 1px, 거의 안 보임)
 * 2. 사용자가 스크롤 → sentinel이 viewport에 진입 → `fetchNextPage()` 자동 호출
 * 3. 새 페이지 데이터 추가 렌더링 → sentinel은 자동으로 더 아래로 밀려남
 *
 * "sentinel"은 무한스크롤 표준 용어로 **"리스트 끝을 알리는 마커"** 를 의미.
 *
 * ─────────────────────────────────────────────────────────────
 * # 페이지 누적 구조
 *
 * sentinel은 **항상 리스트 맨 끝에 1개만** 존재. 새 페이지가 fetch되면 sentinel 앞에 데이터가 추가되며 sentinel은 자동으로 더 아래로 밀려남.
 *
 *   첫 fetch 후 (perPage=50):                  page 2 fetch 후:
 *   ┌───────────────────────┐                  ┌───────────────────────┐
 *   │ 문제 1                │                  │ 문제 1                │
 *   │ ...                   │                  │ ...                   │
 *   │ 문제 50               │ ← page 1 끝      │ 문제 50               │
 *   │ <div sentinel/>       │ ← 항상 맨 끝     │ 문제 51               │
 *   └───────────────────────┘                  │ ...                   │
 *                                              │ 문제 100              │ ← page 2 끝
 *                                              │ <div sentinel/>       │ ← 자동으로 밀려남
 *                                              └───────────────────────┘
 *
 * ─────────────────────────────────────────────────────────────
 * # sentinel 트리거 시점 & rootMargin
 *
 * sentinel은 리스트 맨 끝(맨 아래)에 위치. 사용자가 스크롤해서 sentinel이 viewport에 진입할 때 트리거됨.
 *
 * 단, **`rootMargin` 만큼 viewport 경계를 가상으로 확장**해 sentinel을 미리 감지 → 끊김 없는 UX 제공.
 *
 *   ┌───────────── 가상 viewport 경계 (rootMargin 확장) ─────────────┐
 *   │                                                                  │
 *   │   ┌────────── 실제 viewport ──────────┐                          │
 *   │   │ 문제 47                            │                          │
 *   │   │ 문제 48                            │                          │
 *   │   │ 문제 49                            │                          │
 *   │   │ 문제 50                            │                          │
 *   │   └────────────────────────────────────┘                          │
 *   │   <div sentinel/>  ← 진짜 화면엔 안 보이지만 가상 경계 안에 진입 │
 *   │                       → IntersectionObserver 발화 → fetch 시작   │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * **rootMargin 기준 (참고)**:
 * - `0~100px`: 진짜 끝까지 가야 fetch (데이터 절약, 끝에서 멈칫 가능)
 * - `200~400px`: 일반 리스트 뷰 / 학습 앱에 적합 — **기본값 300px**
 * - `500px+`: 빠른 스크롤 / 무거운 미디어 (불필요 fetch 가능성↑)
 *
 * ─────────────────────────────────────────────────────────────
 * # 사용 예시
 *
 * @example
 *   const scrollContainerRef = useRef<HTMLDivElement | null>(null);
 *   const { sentinelRef } = useInfiniteScroll({
 *     hasNextPage: query.hasNextPage ?? false,
 *     isFetchingNextPage: query.isFetchingNextPage ?? false,
 *     fetchNextPage: query.fetchNextPage,
 *     root: scrollContainerRef,
 *   });
 *
 *   <Scroll containerRef={scrollContainerRef}>
 *     {items}
 *     <div ref={sentinelRef} style={{ height: '1px' }} />
 *   </Scroll>
 */
export const useInfiniteScroll = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  root,
  rootMargin = '300px',
}: IOptions) => {
  // 리스트 끝에 부착할 마커 ref — IntersectionObserver가 이 DOM을 감시
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    // 더 가져올 페이지가 없으면 observer 자체를 설치하지 않음 (불필요한 감시 방지)
    if (!el || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // sentinel이 viewport(또는 rootMargin 확장 영역)에 들어오면 다음 페이지 fetch
        // hasNextPage / isFetchingNextPage 재확인으로 중복 호출 방지
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: root?.current ?? null, rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, root, rootMargin]);

  return { sentinelRef };
};
